import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Heart, Users, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import aboutGames from '../assets/about-games.png';
import aboutSocial from '../assets/about-social.png';
import aboutCompanionship from '../assets/about-companionship.png';
import aboutFootball from '../assets/about-football.png';
import aboutFootball1 from '../assets/about-football1.png';
import aboutPetcare from '../assets/about-petcare.png';
import aboutUnpacking from '../assets/about-unpacking.png';

const carouselImages = [
  { src: aboutCompanionship, alt: 'Care worker and elderly client in companionship visit' },
  { src: aboutGames,         alt: 'Care worker and elderly client playing board games' },
  { src: aboutFootball,      alt: 'Care worker and elderly client watching football together' },
  { src: aboutFootball1,     alt: 'Care worker and elderly client enjoying a match' },
  { src: aboutSocial,        alt: 'Care worker accompanying elderly client to social event' },
  { src: aboutPetcare,       alt: 'Care worker walking dog with elderly client in park' },
  { src: aboutUnpacking,     alt: 'Care worker helping elderly client unpack shopping' },
];

const AboutSection = () => {
  const [current, setCurrent] = useState(0);
  const total = carouselImages.length;

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % total);
  }, [total]);

  const prev = () => {
    setCurrent(prev => (prev - 1 + total) % total);
  };

  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left - Image Carousel */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            {/* Images */}
            <div className="relative h-96 w-full">
              {carouselImages.map((image, index) => (
                <img
                  key={index}
                  src={image.src}
                  alt={image.alt}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    index === current ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
            </div>

            {/* Left Arrow */}
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
            >
              <ChevronLeft className="w-6 h-6 text-slate-700" />
            </button>

            {/* Right Arrow */}
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
            >
              <ChevronRight className="w-6 h-6 text-slate-700" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrent(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                    index === current
                      ? 'bg-white scale-125'
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right - Content */}
          <div className="space-y-6">
            <div>
              <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">About Us</span>
              <h2 className="text-4xl font-bold text-slate-900 mt-2">
                About AFT Senior Assisted Services
              </h2>
            </div>

            <div className="space-y-4 text-lg text-slate-600">
              <p>
                AFT Senior Assisted Services provides compassionate home support for older adults who want to remain independent while receiving practical help and companionship.
              </p>
              <p>
                Our services are designed to support daily living, reduce loneliness, and provide reassurance to families that their loved ones are safe and supported.
              </p>
              <p>
                The service is delivered by an experienced, DBS-checked and insured professional, ensuring safety, reliability and peace of mind.
              </p>
              <p>
                We focus on personalised support, flexible scheduling, and building trusted relationships with every client we serve.
              </p>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-2 gap-6 pt-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-cyan-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">DBS Checked</h4>
                  <p className="text-sm text-slate-600">Fully vetted professional</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Heart className="w-5 h-5 text-teal-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Compassionate</h4>
                  <p className="text-sm text-slate-600">Caring & understanding</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Personalised</h4>
                  <p className="text-sm text-slate-600">Tailored to your needs</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-cyan-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Flexible</h4>
                  <p className="text-sm text-slate-600">Schedule that suits you</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;